
import {
  defineComponent,
  PropType,
  ref,
  watch,
  computed,
  onMounted,
} from "vue";
import { TranslationService } from "@/core/services/TranslationService";
import VZModal from "@/components/content/VZModal.vue";
import { IForm } from "@/interfaces/iForm";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";

import { FormatMethods } from "@/helpers/data-format";
import { usePagination, INNER_PAGE_ROWS_PER_PAGE } from "@/helpers/pagination";

import { IDocuments } from "@/interfaces/iDocument";
import { DocumentApi } from "@/services/api/Document.service";
import moment from "moment";

export default defineComponent({
  components: {
    VZModal,
    Datatable,
  },
  emits: ["close-modal"],
  props: {
    value: { type: Boolean, default: false },
    viewName: { type: String, default: "" },
    data: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    showDialog: { type: Boolean, default: false },
    dateFilter: { type: Object, required: true },
  },
  setup(props, { emit }) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const closeModal = () => {
      emit("close-modal");
      page.value = 1;
      total.value = 0;
      rowsPerPage.value = INNER_PAGE_ROWS_PER_PAGE;
      tableData.value = [];
      localPreference.value = {};
    };

    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const tableLoading = ref(false);
    const showSearch = false;
    const tableData = ref<IDocuments[]>([]);
    const tableHeader = ref([
      {
        name: TranslationService.translate("common.formName"),
        key: "documentName",
      },
      {
        name: TranslationService.translate("common.siteName"),
        key: "siteName",
      },
      {
        name: TranslationService.translate("common.username"),
        key: "userName",
      },
      {
        name: TranslationService.translate("common.startDate"),
        key: "startDate",
      },
      {
        name: TranslationService.translate("common.endDate"),
        key: "endDate",
      },
      {
        name: TranslationService.translate("common.status"),
        key: "status",
      },
    ]);
    const localPreference = ref({});
    const format = computed(() => {
      return FormatMethods;
    });
    onMounted(async () => {
      await queryUpdate({
        ...localPreference.value,
        sort: "startDate,desc",
        size: rowsPerPage.value,
      });
    });
    const queryUpdate = async (params?: Record<string, number | string>) => {
      try {
        tableLoading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "startDate,desc",
          };
        }
        localPreference.value = { ...params };
        const data = await DocumentApi.getInstance().getDocumentsList({
          ...params,
          formDefinitionIds: props.data.webId,
          startDate: props.dateFilter?.startDate
            ? moment(props.dateFilter.startDate)
                .startOf("day")
                .format("YYYY-MM-DDTHH:mm:ssZ")
                .toString()
            : "",
          endDate: props.dateFilter?.endDate
            ? moment(props.dateFilter.endDate)
                .endOf("day")
                .format("YYYY-MM-DDTHH:mm:ssZ")
                .toString()
            : "",
          scheduleStatus: "all",
        });
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } catch (err) {
        console.error("Error fetching documents:", err);
        tableData.value = [];
        total.value = 0;
      } finally {
        tableLoading.value = false;
      }
    };

    watch(
      () => props.data,
      (newData) => {
        console.log("props.data changed:", newData);
        if (newData && newData.webId) {
          queryUpdate({
            ...localPreference.value,
            sort: "startDate,desc",
            size: rowsPerPage.value,
          });
        } else {
          tableData.value = [];
          total.value = 0;
        }
      },
      { immediate: true, deep: true }
    );

    return {
      t,
      closeModal,
      format,
      tableData,
      tableHeader,
      showSearch,
      page,
      total,
      rowsPerPage,
      tableLoading,
      queryUpdate,
    };
  },
});
